import { parseSseChunk, type StreamEvent } from '@ralph/contracts'
import { ApiRequestError, buildHeaders } from './client.ts'

export type StreamOptions = {
  onEvent: (event: StreamEvent) => void
  signal?: AbortSignal
}

/**
 * Reads an SSE response body to completion.
 *
 * `parseSseChunk` returns the leftover partial frame so it can be prepended to the next
 * chunk. Splitting each chunk independently would silently drop any event that straddles a
 * TCP boundary — which is precisely the large ones (a full `retrieval.completed` payload,
 * a `message.completed` with citations).
 */
const consume = async (response: Response, options: StreamOptions): Promise<void> => {
  if (!response.body) throw new Error('the server returned no stream body')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const parsed = parseSseChunk(buffer)
      buffer = parsed.rest
      for (const event of parsed.events) options.onEvent(event)
    }
  } finally {
    reader.releaseLock()
  }
}

/** Sends a message and streams the run. Resolves when the stream closes. */
export const streamMessage = async (
  conversationId: string,
  body: {
    content: string
    idempotencyKey?: string
  },
  options: StreamOptions
): Promise<void> => {
  const headers = buildHeaders({ 'content-type': 'application/json', accept: 'text/event-stream' })

  const response = await fetch(`/api/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    credentials: 'same-origin',
    ...(options.signal ? { signal: options.signal } : {})
  })

  if (!response.ok) {
    const detail = await response.json().catch(() => null)
    throw new ApiRequestError(
      response.status,
      detail?.issues ?? [{ code: 'stream_failed', message: response.statusText, field: null, retryable: false }],
      detail?.requestId ?? ''
    )
  }

  await consume(response, options)
}

/**
 * Reattaches to a run, replaying everything after `afterSeq`.
 *
 * This is what makes a dropped connection recoverable rather than merely survivable: the
 * server persists every event before flushing it, so the client can always ask for the
 * exact gap it missed.
 */
export const resumeRunStream = async (runId: string, afterSeq: number, options: StreamOptions): Promise<void> => {
  const response = await fetch(`/api/runs/${runId}/stream?after=${afterSeq}`, {
    headers: buildHeaders({ accept: 'text/event-stream' }),
    credentials: 'same-origin',
    ...(options.signal ? { signal: options.signal } : {})
  })

  if (!response.ok) throw new Error(`failed to reattach to run ${runId}: ${response.status}`)
  await consume(response, options)
}
